import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HiDownload,
  HiEye,
  HiOutlineCash,
  HiOutlineClipboardCheck,
  HiOutlineCurrencyDollar,
  HiOutlinePlus,
  HiOutlineUserGroup,
  HiPencil,
  HiRefresh,
  HiSparkles,
  HiX,
} from 'react-icons/hi';
import { PageLayout } from '../../components/layout';
import { SearchInput } from '../../components/Search';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { Table } from '../../components/Table';
import { TableColumn } from '../../components/Table/Table.types';
import Pagination from '../../components/Pagination';
import { createManualFund, getFunds, updateFund } from '../../services/FundService';
import type { FundRecord, FundsSummary, FundStatus, FundType } from '../../types/fund.types';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { downloadCsv } from '../../utils/csvDownload';
import { formatDate } from '../../utils/dateFormatter';

const PAGE_SIZE = 20;

const summaryCardClassName =
  'rounded-2xl border border-white/60 dark:border-white/10 bg-white/75 dark:bg-slate-900/55 backdrop-blur-sm px-5 py-4 shadow-sm';

export const FundsPageStandardized: React.FC = () => {
  const [funds, setFunds] = useState<FundRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [resetPagination] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<FundsSummary | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [fundingTypeFilter, setFundingTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Details & Edit Modal
  const [selectedFund, setSelectedFund] = useState<FundRecord | null>(null);
  const [editStatus, setEditStatus] = useState<string>('');
  const [editNotes, setEditNotes] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Manual Add Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [addAmount, setAddAmount] = useState<string>('');
  const [addFundingType, setAddFundingType] = useState<FundType>('DIRECT_PAY');
  const [addStatus, setAddStatus] = useState<FundStatus>('SUCCEEDED');
  const [addFullName, setAddFullName] = useState<string>('');
  const [addEmail, setAddEmail] = useState<string>('');
  const [addGivingThrough, setAddGivingThrough] = useState<string>('Personal');
  const [addTier, setAddTier] = useState<string>('');
  const [addPaymentMethod, setAddPaymentMethod] = useState<string>('');
  const [addNotes, setAddNotes] = useState<string>('');
  const [isSubmittingManual, setIsSubmittingManual] = useState<boolean>(false);

  const handleOpenAddModal = () => {
    setAddAmount('');
    setAddFundingType('DIRECT_PAY');
    setAddStatus('SUCCEEDED');
    setAddFullName('');
    setAddEmail('');
    setAddGivingThrough('Personal');
    setAddTier('');
    setAddPaymentMethod('');
    setAddNotes('');
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleFundingTypeChange = (type: FundType) => {
    setAddFundingType(type);
    if (type === 'PLEDGE') {
      setAddStatus('PLEDGED');
      setAddGivingThrough('Personal');
      setAddPaymentMethod('PLEDGE');
    } else if (type === 'DAFSHARE') {
      setAddStatus('SUCCEEDED');
      setAddGivingThrough('Donor-advised fund');
      setAddPaymentMethod('DAFShare');
    } else {
      setAddStatus('SUCCEEDED');
      setAddGivingThrough('Personal');
      setAddPaymentMethod('Direct Pay');
    }
  };

  const handleCreateManualFund = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(addAmount);
    if (!parsedAmount || parsedAmount <= 0) {
      showErrorToast('Please enter a valid contribution amount.');
      return;
    }

    setIsSubmittingManual(true);
    try {
      await createManualFund({
        amount: parsedAmount,
        currency: 'USD',
        fundingType: addFundingType,
        status: addStatus,
        fullName: addFullName.trim() || undefined,
        email: addEmail.trim() || undefined,
        tier: addTier.trim() || undefined,
        givingThrough: addGivingThrough.trim() || undefined,
        paymentMethod: addPaymentMethod.trim() || undefined,
        giftDesignation: 'Tap2Feed Global Hunger Network — Restricted Founding Campaign',
        programCode: 'USA005',
        notes: addNotes.trim() || undefined,
      });

      showSuccessToast('Fund record created successfully');
      setIsAddModalOpen(false);
      fetchFundsList(1);
      setCurrentPage(1);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to create fund record');
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const fetchFundsList = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    try {
      const response = await getFunds({
        page,
        limit: PAGE_SIZE,
        search: searchTerm || undefined,
        fundingType: fundingTypeFilter || undefined,
        status: statusFilter || undefined,
        programCode: 'USA005',
      });

      setFunds(response.data || []);
      setTotalRecords(response.total || 0);
      setSummary(response.summary || null);
    } catch (error: any) {
      console.error('Error loading funds:', error);
      showErrorToast(error.message || 'Failed to load funds');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, fundingTypeFilter, statusFilter]);

  useEffect(() => {
    fetchFundsList(currentPage);
  }, [fetchFundsList, currentPage]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleOpenDetails = (fund: FundRecord) => {
    setSelectedFund(fund);
    setEditStatus(fund.status);
    setEditNotes(fund.notes || '');
  };

  const handleCloseDetails = () => {
    setSelectedFund(null);
  };

  const handleSaveFundUpdate = async () => {
    if (!selectedFund) return;
    setIsUpdating(true);
    try {
      await updateFund(selectedFund.id, {
        status: editStatus as any,
        notes: editNotes,
      });

      showSuccessToast('Fund updated successfully');
      setSelectedFund(null);
      // Refresh list
      fetchFundsList(currentPage);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to update fund');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExportCsv = () => {
    if (!funds.length) {
      showErrorToast('No funds to export');
      return;
    }

    const rows = funds.map((f) => ({
      ID: f.id,
      Date: new Date(f.createdAt).toISOString(),
      'Full Name': f.fullName,
      Email: f.email,
      Type: f.fundingType,
      Status: f.status,
      Tier: f.tier || '',
      'Amount ($)': f.amount,
      Currency: f.currency,
      'Giving Through': f.givingThrough || '',
      'Help Interests': Array.isArray(f.helpInterests) ? f.helpInterests.join('; ') : '',
      'Program Code': f.programCode,
      'Stripe Session ID': f.stripeSessionId || '',
      'Stripe Payment Intent': f.stripePaymentIntentId || '',
      Notes: f.notes || '',
    }));

    downloadCsv(`founding_partner_funds_${new Date().toISOString().slice(0, 10)}.csv`, rows);
    showSuccessToast('Funds CSV downloaded');
  };

  const columns: TableColumn<FundRecord>[] = useMemo(
    () => [
      {
        id: 'createdAt',
        label: 'Date',
        field: 'createdAt',
        sortable: true,
        format: (_, fund: FundRecord) => (
          <div className="text-xs text-slate-700 dark:text-slate-300">
            <div className="font-medium">{formatDate(fund.createdAt)}</div>
            <div className="text-[10px] text-slate-400">
              {new Date(fund.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ),
      },
      {
        id: 'partner',
        label: 'Partner',
        field: 'fullName',
        sortable: true,
        format: (_, fund: FundRecord) => (
          <div className="max-w-[220px]">
            <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
              {fund.fullName}
            </div>
            <div className="text-xs text-slate-500 truncate">{fund.email}</div>
            {fund.givingThrough && (
              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {fund.givingThrough}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'fundingType',
        label: 'Type',
        field: 'fundingType',
        format: (_, fund: FundRecord) => {
          let badgeClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300';
          let label = 'Direct Pay';

          if (fund.fundingType === 'PLEDGE') {
            badgeClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300';
            label = 'Pledge';
          } else if (fund.fundingType === 'DAFSHARE') {
            badgeClass = 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300';
            label = 'DAFShare';
          }

          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${badgeClass}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        id: 'tier',
        label: 'Tier / Designation',
        field: 'tier',
        format: (_, fund: FundRecord) => (
          <div className="text-xs max-w-[200px]">
            <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">
              {fund.tier || 'Custom Amount'}
            </div>
            <div className="text-[10px] text-slate-500 truncate">
              {fund.programCode} — Tap2Feed
            </div>
          </div>
        ),
      },
      {
        id: 'amount',
        label: 'Amount',
        field: 'amount',
        sortable: true,
        format: (_, fund: FundRecord) => (
          <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
            ${fund.amount?.toLocaleString()} {fund.currency}
          </div>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        field: 'status',
        format: (_, fund: FundRecord) => {
          let variant: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default';
          if (fund.status === 'SUCCEEDED') variant = 'success';
          else if (fund.status === 'PLEDGED') variant = 'info';
          else if (fund.status === 'PENDING') variant = 'warning';
          else if (fund.status === 'FAILED') variant = 'error';

          return <Badge label={fund.status} variant={variant} size="sm" />;
        },
      },
      {
        id: 'actions',
        label: 'Actions',
        field: 'id',
        format: (_, fund: FundRecord) => (
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleOpenDetails(fund)}
              className="text-xs flex items-center gap-1 py-1 px-2.5"
            >
              <HiEye className="w-3.5 h-3.5" />
              <span>Details</span>
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <PageLayout
      title="Founding Partner Funds & Pledges"
      subtitle="Manage and track Founding Partner direct contributions and pledges for Program USA005."
    >
      <div className="space-y-6">
        {/* Top Summary Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={summaryCardClassName}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                Total Committed
              </span>
              <HiSparkles className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
              ${summary?.totalCommitted?.toLocaleString() || '0'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Goal: $1M Phase 1 / $5M Full
            </div>
          </div>

          <div className={summaryCardClassName}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider font-semibold text-emerald-600">
                Direct Contributed
              </span>
              <HiOutlineCash className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 mt-1">
              ${summary?.totalDirectPaidAmount?.toLocaleString() || '0'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {summary?.totalDirectPayCount || 0} Paid Contributions
            </div>
          </div>

          <div className={summaryCardClassName}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider font-semibold text-indigo-600">
                Pledged Amount
              </span>
              <HiOutlineClipboardCheck className="w-5 h-5 text-indigo-500" />
            </div>
            <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400 mt-1">
              ${summary?.totalPledgedAmount?.toLocaleString() || '0'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {summary?.totalPledgeCount || 0} Pledges Registered
            </div>
          </div>

          <div className={summaryCardClassName}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider font-semibold text-teal-600">
                Total Submissions
              </span>
              <HiOutlineUserGroup className="w-5 h-5 text-teal-500" />
            </div>
            <div className="text-2xl font-bold text-teal-700 dark:text-teal-400 mt-1">
              {summary?.totalRecords || 0}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              USA005 Founding Campaign
            </div>
          </div>
        </div>

        {/* Action & Filter Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white/60 dark:bg-slate-900/40 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="w-full sm:w-72">
              <SearchInput
                placeholder="Search name, email, tier, or session..."
                onSearch={handleSearch}
                value={searchTerm}
              />
            </div>

            <select
              value={fundingTypeFilter}
              onChange={(e) => {
                setFundingTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 text-xs font-medium rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none"
            >
              <option value="">All Funding Types</option>
              <option value="DIRECT_PAY">Direct Pay</option>
              <option value="PLEDGE">Pledge</option>
              <option value="DAFSHARE">DAFShare</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 text-xs font-medium rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="SUCCEEDED">Succeeded</option>
              <option value="PLEDGED">Pledged</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={handleOpenAddModal}
              className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <HiOutlinePlus className="w-4 h-4" />
              <span>Add Manual Fund</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchFundsList(currentPage)}
              className="flex items-center gap-1 text-xs"
            >
              <HiRefresh className="w-4 h-4" />
              <span>Refresh</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportCsv}
              className="flex items-center gap-1 text-xs"
            >
              <HiDownload className="w-4 h-4" />
              <span>Export CSV</span>
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
          <Table
            columns={columns}
            data={funds}
            isLoading={isLoading}
            emptyMessage="No founding partner funds or pledges found."
          />

          {totalRecords > PAGE_SIZE && (
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <span className="text-xs text-slate-500">
                Showing {funds.length} of {totalRecords} entries
              </span>
              <Pagination
                totalRecords={totalRecords}
                perPageCount={PAGE_SIZE}
                reset={resetPagination}
                setPerPageDataSet={(start) => {
                  const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE) + 1);
                  setCurrentPage(nextPage);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Details & Edit Drawer/Modal */}
      {selectedFund &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                <div>
                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                    {selectedFund.programCode} — {selectedFund.fundingType}
                  </span>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Founding Partner Submission #{selectedFund.id}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseDetails}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <HiX className="w-6 h-6" />
                </button>
              </div>

              {/* Submission Information Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl text-xs sm:text-sm">
                <div>
                  <span className="text-slate-500 block">Partner Full Name</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {selectedFund.fullName}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Email Address</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {selectedFund.email}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Giving Through</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {selectedFund.givingThrough || 'Personal'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Tier</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {selectedFund.tier || 'Custom'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Total Amount</span>
                  <span className="text-base font-bold text-emerald-600">
                    ${selectedFund.amount?.toLocaleString()} {selectedFund.currency}
                  </span>
                </div>
              </div>

              {/* How would you like to help (Interests) */}
              {Array.isArray(selectedFund.helpInterests) && selectedFund.helpInterests.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold uppercase text-slate-500 tracking-wider">
                    How would you like to help (Selected Interests)
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {selectedFund.helpInterests.map((interest, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-xs font-medium"
                      >
                        ✓ {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Gift Designation */}
              {selectedFund.giftDesignation && (
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  <span className="font-semibold text-slate-700 dark:text-slate-300">Designation:</span>{' '}
                  {selectedFund.giftDesignation}
                </div>
              )}

              {/* Stripe Details */}
              {(selectedFund.stripeSessionId || selectedFund.stripePaymentIntentId) && (
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-2 text-xs bg-white dark:bg-slate-900">
                  <div className="font-semibold text-slate-800 dark:text-slate-200">
                    Stripe Transaction Details
                  </div>
                  {selectedFund.stripeSessionId && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Checkout Session:</span>
                      <span className="font-mono text-slate-700 dark:text-slate-300 truncate max-w-[280px]">
                        {selectedFund.stripeSessionId}
                      </span>
                    </div>
                  )}
                  {selectedFund.stripePaymentIntentId && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payment Intent:</span>
                      <span className="font-mono text-slate-700 dark:text-slate-300">
                        {selectedFund.stripePaymentIntentId}
                      </span>
                    </div>
                  )}
                  {selectedFund.stripePaymentStatus && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Stripe Payment Status:</span>
                      <span className="font-semibold text-emerald-600">
                        {selectedFund.stripePaymentStatus}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Edit Status & Notes Form */}
              <div className="space-y-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Update Status & Follow-up Notes
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    >
                      <option value="SUCCEEDED">SUCCEEDED (Paid)</option>
                      <option value="PLEDGED">PLEDGED (Registered)</option>
                      <option value="PENDING">PENDING</option>
                      <option value="FAILED">FAILED</option>
                      <option value="CANCELLED">CANCELLED</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Internal Notes / Follow-up History
                  </label>
                  <textarea
                    rows={3}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Enter notes on wire receipt, partner outreach, or meeting details..."
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
                <Button variant="secondary" size="sm" onClick={handleCloseDetails}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveFundUpdate}
                  loading={isUpdating}
                  className="flex items-center gap-1"
                >
                  <HiPencil className="w-4 h-4" />
                  <span>Save Changes</span>
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Manual Add Fund Modal */}
      {isAddModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                <div>
                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                    Program USA005
                  </span>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Add Manual Fund / Pledge Record
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseAddModal}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <HiX className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateManualFund} className="space-y-4">
                {/* Funding Type Selector */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wider">
                    Contribution Type *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'DIRECT_PAY', label: 'Direct Pay' },
                      { key: 'PLEDGE', label: 'Pledge' },
                      { key: 'DAFSHARE', label: 'DAFShare' },
                    ].map((t) => {
                      const isSelected = addFundingType === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => handleFundingTypeChange(t.key as FundType)}
                          className={`py-2.5 px-3 rounded-xl text-xs font-bold border text-center transition cursor-pointer ${
                            isSelected
                              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                              : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Amount & Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Amount ($ USD) *
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold">$</span>
                      <input
                        type="number"
                        min="1"
                        step="any"
                        value={addAmount}
                        onChange={(e) => setAddAmount(e.target.value)}
                        placeholder="e.g. 10000"
                        className="w-full pl-8 pr-4 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Initial Status
                    </label>
                    <select
                      value={addStatus}
                      onChange={(e) => setAddStatus(e.target.value as FundStatus)}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                    >
                      <option value="SUCCEEDED">SUCCEEDED (Received/Completed)</option>
                      <option value="PLEDGED">PLEDGED (Registered)</option>
                      <option value="PENDING">PENDING</option>
                      <option value="FAILED">FAILED</option>
                    </select>
                  </div>
                </div>

                {/* Optional Donor Information (Anonymous Supported) */}
                <div className="border border-slate-200/80 dark:border-slate-800 rounded-xl p-4 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      Partner Details (Optional — Can be Anonymous)
                    </span>
                    <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                      Optional
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-1">
                        Full Name (Leave blank for Anonymous)
                      </label>
                      <input
                        type="text"
                        value={addFullName}
                        onChange={(e) => setAddFullName(e.target.value)}
                        placeholder="e.g. Jane Doe (or leave blank)"
                        className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={addEmail}
                        onChange={(e) => setAddEmail(e.target.value)}
                        placeholder="e.g. donor@foundation.org"
                        className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Giving Entity & Partner Tier */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Giving Through
                    </label>
                    <select
                      value={addGivingThrough}
                      onChange={(e) => setAddGivingThrough(e.target.value)}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                    >
                      <option value="Personal">Personal</option>
                      <option value="Foundation">Foundation</option>
                      <option value="Company">Company</option>
                      <option value="Donor-advised fund">Donor-advised fund</option>
                      <option value="Family Trust">Family Trust</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Tier / Designation Label (Optional)
                    </label>
                    <input
                      type="text"
                      value={addTier}
                      onChange={(e) => setAddTier(e.target.value)}
                      placeholder="e.g. FOUNDING PARTNER"
                      className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Payment Method / Reference */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Payment Method / Reference Note (Optional)
                  </label>
                  <input
                    type="text"
                    value={addPaymentMethod}
                    onChange={(e) => setAddPaymentMethod(e.target.value)}
                    placeholder="e.g. Wire Transfer, Check #1042, DAF Fidelity, etc."
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Internal Notes / Comments
                  </label>
                  <textarea
                    rows={2}
                    value={addNotes}
                    onChange={(e) => setAddNotes(e.target.value)}
                    placeholder="Add any internal admin notes or receipt details..."
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none"
                  />
                </div>

                {/* Form Buttons */}
                <div className="flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 pt-4">
                  <Button variant="secondary" size="sm" type="button" onClick={handleCloseAddModal}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    loading={isSubmittingManual}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <HiOutlinePlus className="w-4 h-4" />
                    <span>Create Record</span>
                  </Button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </PageLayout>
  );
};

export default FundsPageStandardized;
